import { useState } from 'react';
import { toast } from 'sonner';
import { itineraryApi } from '../services/api';

const NATIONALITY_CURRENCY_MAP = {
  'australian': 'AUD', 'american': 'USD', 'british': 'GBP', 'canadian': 'CAD',
  'indian': 'INR', 'japanese': 'JPY', 'chinese': 'CNY', 'korean': 'KRW',
  'singaporean': 'SGD', 'malaysian': 'MYR', 'thai': 'THB', 'indonesian': 'IDR',
  'filipino': 'PHP', 'vietnamese': 'VND', 'european': 'EUR', 'german': 'EUR',
  'french': 'EUR', 'italian': 'EUR', 'spanish': 'EUR', 'dutch': 'EUR',
  'new zealander': 'NZD', 'south african': 'ZAR', 'brazilian': 'BRL', 'mexican': 'MXN',
  'swiss': 'CHF', 'swedish': 'SEK', 'norwegian': 'NOK', 'danish': 'DKK',
  'polish': 'PLN', 'czech': 'CZK', 'hungarian': 'HUF', 'turkish': 'TRY',
  'emirati': 'AED', 'saudi': 'SAR', 'qatari': 'QAR', 'kuwaiti': 'KWD',
  'russian': 'RUB', 'ukrainian': 'UAH', 'egyptian': 'EGP', 'nigerian': 'NGN',
  'kenyan': 'KES', 'ghanaian': 'GHS', 'colombian': 'COP', 'peruvian': 'PEN',
  'chilean': 'CLP', 'argentine': 'ARS', 'sri lankan': 'LKR', 'bangladeshi': 'BDT',
  'pakistani': 'PKR', 'nepalese': 'NPR', 'myanmar': 'MMK', 'cambodian': 'KHR',
};

export const useCurrencyConversion = (tripInput, generatedItinerary) => {
  const [currencyMode, setCurrencyMode] = useState('destination');
  const [convertedBudget, setConvertedBudget] = useState(null);
  const [convertingCurrency, setConvertingCurrency] = useState(false);

  const getNationalityCurrency = (nationality) =>
    NATIONALITY_CURRENCY_MAP[(nationality || '').toLowerCase()] || 'USD';

  const detectBudgetCurrency = () => {
    const budget = generatedItinerary?.estimated_total_budget;
    if (!budget) return 'USD';
    if (budget.currency) {
      const code = budget.currency.toUpperCase().replace(/[^A-Z]/g, '');
      if (code.length === 3) return code;
    }
    const totalStr = budget.total || budget.accommodation || '';
    const currMatch = String(totalStr).match(/([A-Z]{3})/);
    if (currMatch) return currMatch[1];
    if (String(totalStr).includes('$')) return 'USD';
    if (String(totalStr).includes('\u00a5')) return 'JPY';
    if (String(totalStr).includes('\u20ac')) return 'EUR';
    if (String(totalStr).includes('\u00a3')) return 'GBP';
    return 'USD';
  };

  const toggleCurrency = async () => {
    if (!generatedItinerary?.estimated_total_budget) return;
    if (currencyMode === 'home' && convertedBudget) {
      setCurrencyMode('destination');
      return;
    }
    const budget = generatedItinerary.estimated_total_budget;
    const destCurrency = detectBudgetCurrency();
    const homeCurrency = getNationalityCurrency(tripInput?.nationality);
    if (homeCurrency === destCurrency) {
      toast.info(`Budget is already in ${homeCurrency}`);
      return;
    }
    setConvertingCurrency(true);
    try {
      const res = await itineraryApi.convertCurrency(1, destCurrency, homeCurrency);
      if (res.data.success && res.data.rate) {
        const rate = res.data.rate;
        const extractFirstNum = (str) => {
          if (!str) return NaN;
          const match = String(str).match(/[\d,]+(?:\.\d+)?/);
          if (!match) return NaN;
          return parseFloat(match[0].replace(/,/g, ''));
        };
        const convertAmount = (str) => {
          if (!str) return str;
          const num = extractFirstNum(str);
          if (isNaN(num)) return str;
          const converted = `${homeCurrency} ${Math.round(num * rate).toLocaleString()}`;
          // Preserve description text in parentheses (e.g., "(for 4 adults, 6 nights...)")
          const descMatch = String(str).match(/\((?!approx)[^)]+\)/);
          if (descMatch) return `${converted} ${descMatch[0]}`;
          return converted;
        };
        setConvertedBudget({
          accommodation: convertAmount(budget.accommodation),
          food: convertAmount(budget.food),
          flights: convertAmount(budget.flights),
          transport: convertAmount(budget.transport),
          activities: convertAmount(budget.activities),
          total: convertAmount(budget.total),
          currency: homeCurrency,
          rate: rate,
          fromCurrency: destCurrency,
        });
        setCurrencyMode('home');
      } else {
        toast.error('Currency conversion unavailable');
      }
    } catch {
      toast.error('Failed to convert currency');
    } finally {
      setConvertingCurrency(false);
    }
  };

  return {
    currencyMode, convertedBudget, convertingCurrency,
    toggleCurrency, getNationalityCurrency, detectBudgetCurrency,
    trip_input: tripInput,
  };
};
